import * as THREE from 'three';

/**
 * AUTO SMOOTH NORMALS (2026-09-01, user: "all my mesh I want auto smooth — I forgot to do that
 * in Blender. Can we post-process or do I need to do it from Blender?").
 *
 * This is the code-side equivalent of Blender's "Shade Auto Smooth": every vertex normal is
 * re-averaged across ALL faces that meet at the same POSITION (not just the same vertex slot —
 * a flat-shaded export duplicates vertices per face, so slot-based `computeVertexNormals` would
 * just reproduce the flat normals), except across edges sharper than `creaseAngleDeg`, which
 * stay hard exactly like Blender's angle threshold.
 *
 * Deliberately IN-PLACE and INDEX-PRESERVING, unlike three's `toCreasedNormals` utility, which
 * returns a de-indexed clone: the auto-LOD system swaps meshopt-simplified INDEX buffers on
 * these same geometries, and de-indexing them would break that (and triple the vertex memory).
 * Only the `normal` attribute is rewritten; positions, UVs and indices are untouched, so every
 * downstream consumer (baked materials, physics hulls, LOD, reflections) is unaffected.
 *
 * Face normals are accumulated UN-normalized (the raw cross product = 2·area·n̂), which gives
 * the area weighting that keeps large faces from being bent by slivers — the same weighting
 * `computeVertexNormals` uses.
 */
export function autoSmoothNormals(geometry: THREE.BufferGeometry, creaseAngleDeg: number): void {
  const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!position || position.count === 0) return;

  const index = geometry.getIndex();
  const vertexCount = position.count;
  const triCount = index ? index.count / 3 : vertexCount / 3;
  if (triCount < 1) return;

  const cosCrease = Math.cos(THREE.MathUtils.degToRad(creaseAngleDeg));

  // --- 1. Face normals (un-normalized = area-weighted) + slot → adjacent-tris map -----------
  const faceNormals = new Float32Array(triCount * 3);
  const slotTris: number[][] = Array.from({ length: vertexCount }, () => []);
  const pA = new THREE.Vector3();
  const pB = new THREE.Vector3();
  const pC = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();
  for (let t = 0; t < triCount; t += 1) {
    const a = index ? index.getX(t * 3) : t * 3;
    const b = index ? index.getX(t * 3 + 1) : t * 3 + 1;
    const c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    pA.fromBufferAttribute(position, a);
    pB.fromBufferAttribute(position, b);
    pC.fromBufferAttribute(position, c);
    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab);
    faceNormals[t * 3] = cb.x;
    faceNormals[t * 3 + 1] = cb.y;
    faceNormals[t * 3 + 2] = cb.z;
    slotTris[a]!.push(t);
    slotTris[b]!.push(t);
    slotTris[c]!.push(t);
  }

  // --- 2. Cluster slots by (quantized) position, and collect each cluster's tri set ---------
  // Flat-shaded/UV-split exports duplicate a corner into several slots; the cluster is what
  // reunites them so smoothing happens across the real surface, not per smoothing island.
  const clusterOf = new Map<string, number>();
  const slotCluster = new Uint32Array(vertexCount);
  const clusterTris: number[][] = [];
  for (let v = 0; v < vertexCount; v += 1) {
    const key =
      `${Math.round(position.getX(v) * 1e4)},` +
      `${Math.round(position.getY(v) * 1e4)},` +
      `${Math.round(position.getZ(v) * 1e4)}`;
    let cl = clusterOf.get(key);
    if (cl === undefined) {
      cl = clusterTris.length;
      clusterOf.set(key, cl);
      clusterTris.push([]);
    }
    slotCluster[v] = cl;
    const bucket = clusterTris[cl]!;
    for (const t of slotTris[v]!) {
      if (!bucket.includes(t)) bucket.push(t); // dedup — a tri can reach a cluster via 2 slots
    }
  }

  // --- 3. Per slot: average the cluster's face normals that lie within the crease angle -----
  // The slot's OWN face-normal average is the reference for the angle test, so a slot on the
  // flat side of a hard edge gathers only its side's faces and the edge stays crisp.
  let normal = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (!normal || normal.count !== vertexCount) {
    normal = new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3);
    geometry.setAttribute('normal', normal);
  }
  const base = new THREE.Vector3();
  const sum = new THREE.Vector3();
  const fn = new THREE.Vector3();
  for (let v = 0; v < vertexCount; v += 1) {
    base.set(0, 0, 0);
    for (const t of slotTris[v]!) base.add(fn.fromArray(faceNormals, t * 3));
    if (base.lengthSq() < 1e-12) continue; // degenerate slot — leave its normal alone
    base.normalize();

    sum.set(0, 0, 0);
    for (const t of clusterTris[slotCluster[v]!]!) {
      fn.fromArray(faceNormals, t * 3);
      const len = fn.length();
      if (len < 1e-12) continue;
      if (fn.dot(base) / len >= cosCrease) sum.add(fn);
    }
    if (sum.lengthSq() < 1e-12) continue;
    sum.normalize();
    normal.setXYZ(v, sum.x, sum.y, sum.z);
  }
  normal.needsUpdate = true;
}
